import { FC, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import Button from "components/Button/Button"
import { Flex } from "components/Flex"
import Form from "components/Form/Form"
import FormField from "components/FormField/FormField"
import Input from "components/Input/Input"
import List, { IListItem } from "components/List/List"
import Modal from "components/Modal/Modal"
import NoValue from "components/NoValue/NoValue"
import Spinner from "components/Spinner/Spinner"
import Text from "components/Text/Text"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import AssetService from "services/AssetService"
import DataService from "services/DataService"
import CustomError from "models/CustomError"
import InvoiceByOrderNumberLookup from "models/InvoiceByOrderNumberLookup"
import { ErrorCode } from "config/constants"
import toastMessages from "stores/toastMessages"
import { IApiLink } from "@/types"

const ORDER_NUMBER_MAX_LENGTH = 255

interface IEditOrderNumberModalProps {
    link: IApiLink
    currentOrderNumber?: string
    onSaved(): void | Promise<void>
    onClose(): void
}

type Mode = "input" | "confirming"

const EditOrderNumberModal: FC<IEditOrderNumberModalProps> = ({
    link,
    currentOrderNumber = "",
    onSaved,
    onClose,
}) => {
    const { t } = useTranslation()
    const [mode, setMode] = useState<Mode>("input")
    const [value, setValue] = useState(currentOrderNumber)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [conflictError, setConflictError] = useState<string>()

    // lookup states:
    //   undefined = not loaded yet (spinner)
    //   null      = 404 or lookup failed — no matching invoice to display
    //   value     = matching invoice metadata
    const [lookup, setLookup] = useState<InvoiceByOrderNumberLookup | null | undefined>(undefined)

    const trimmed = value.trim()
    const isEmpty = trimmed.length === 0
    const isUnchanged = trimmed === currentOrderNumber.trim()

    const fetchLookup = useCallback(async (orderNumber: string) => {
        setLookup(undefined)
        try {
            const result = await DataService.getInvoiceByOrderNumber(orderNumber)
            setLookup(result)
        } catch {
            // Lookup failure is non-blocking: proceed to confirm without table
            setLookup(null)
        }
    }, [])

    useEffect(() => {
        if (mode !== "confirming") return
        fetchLookup(trimmed)
    }, [mode, trimmed, fetchLookup])

    function handleSave() {
        setIsSubmitted(true)
        setConflictError(undefined)
        if (isEmpty || isUnchanged) return
        setMode("confirming")
    }

    function backToInput() {
        setMode("input")
        setLookup(undefined)
    }

    async function handleConfirm() {
        setIsSubmitting(true)
        try {
            await AssetService.patchOutgoingInvoiceOrderNumber(link, trimmed)
            await onSaved()
            onClose()
        } catch (e) {
            setIsSubmitting(false)
            backToInput()
            if (e instanceof CustomError && e.code === ErrorCode.conflict) {
                setConflictError(t("outgoingInvoiceControls.editOrderNumberConflict"))
            } else {
                toastMessages.showSystemError()
            }
        }
    }

    if (mode === "confirming") {
        const foundInvoice = lookup ?? null
        const pending = <NoValue>{t("common.pending")}</NoValue>
        const lookupItems: IListItem[] = [
            {
                label: t("outgoingInvoiceControls.editOrderNumberConfirmInvoiceNo"),
                value: foundInvoice?.invoiceNumber
                    ? <Text bold>{foundInvoice.invoiceNumber}</Text>
                    : pending,
            },
            {
                label: t("outgoingInvoiceControls.editOrderNumberConfirmInvoiceDate"),
                value: foundInvoice?.invoiceDate
                    ? <Text bold>{foundInvoice.invoiceDate.format(t("config.dateLongFormat"))}</Text>
                    : pending,
            },
            {
                label: t("outgoingInvoiceControls.editOrderNumberConfirmCreatedOn"),
                value: foundInvoice?.createdOn
                    ? <Text bold>{foundInvoice.createdOn.format(t("config.dateLongTimeFormat"))}</Text>
                    : pending,
            },
        ]

        return (
            <Modal
                title={t("outgoingInvoiceControls.editOrderNumberConfirmTitle")}
                maxWidth="520px"
                onClose={isSubmitting ? undefined : backToInput}
                footer={
                    <Flex justifyContent="flex-end">
                        <Button
                            theme="secondary"
                            label={t("common.cancel")}
                            onClick={backToInput}
                            disabled={isSubmitting}
                        />
                        <Button
                            className="ml-2"
                            label={t("common.confirm")}
                            onClick={handleConfirm}
                            disabled={isSubmitting || lookup === undefined}
                            loading={isSubmitting}
                        />
                    </Flex>
                }>
                <p>{t("outgoingInvoiceControls.editOrderNumberConfirmText")}</p>
                {lookup === undefined &&
                    <Spinner className="py-4" />
                }
                {lookup !== undefined &&
                    <div className="mt-3">
                        {!!foundInvoice &&
                            <Text className="mb-2">
                                {t("outgoingInvoiceControls.editOrderNumberConfirmFoundHeader")}
                            </Text>
                        }
                        <List items={lookupItems} />
                    </div>
                }
            </Modal>
        )
    }

    const isInputInvalid = isSubmitted && (isEmpty || isUnchanged)

    return (
        <Modal
            title={t("outgoingInvoiceControls.editOrderNumberTitle")}
            maxWidth="520px"
            onClose={onClose}
            footer={
                <Flex justifyContent="flex-end">
                    <Button
                        theme="secondary"
                        label={t("common.cancel")}
                        onClick={onClose}
                    />
                    <Button
                        className="ml-2"
                        label={t("common.save")}
                        onClick={handleSave}
                    />
                </Flex>
            }>
            <Form onSubmit={handleSave}>
                <p>{t("outgoingInvoiceControls.editOrderNumberInfo")}</p>
                <FormField label={t("outgoingInvoices.orderNo")}>
                    <Input
                        value={value}
                        onChange={e => {
                            setConflictError(undefined)
                            setValue(e.target.value)
                        }}
                        maxLength={ORDER_NUMBER_MAX_LENGTH}
                        isInvalid={isInputInvalid || !!conflictError}
                        autoFocus
                    />
                    {isSubmitted && isEmpty &&
                        <ValidationMessage>{t("validation.required")}</ValidationMessage>
                    }
                    {isSubmitted && !isEmpty && isUnchanged &&
                        <ValidationMessage>{t("outgoingInvoiceControls.editOrderNumberUnchanged")}</ValidationMessage>
                    }
                    {!!conflictError &&
                        <ValidationMessage>{conflictError}</ValidationMessage>
                    }
                </FormField>
            </Form>
        </Modal>
    )
}

export default EditOrderNumberModal
